/* MolCellQuest 問題データ — DNA・染色体・複製・修復
   ──────────────────────────────────────────────────────────
   ★ この配列に問題オブジェクトを追記すれば、そのままアプリに反映されます。
     id は全ファイルを通して一意にしてください（npm run validate で検査）。
   ★ 必須: id, track, category, categoryName, title, diff, tags,
            explain, type, options(radio), answer, hints, solution
   ★ diff : 80〜320。基礎 0-179 / 標準 180-269 / 応用 270+
   ────────────────────────────────────────────────────────── */

const CAT = { track: "genome", category: "dna", categoryName: "DNA・染色体・複製・修復" };

export const GENOME_DNA = [
  {
    id: "dna01", ...CAT,
    title: "シャルガフの法則", diff: 90, tags: ["塩基組成", "塩基対合"],
    explain: `<p>ある二本鎖DNAの塩基組成を調べたところ、アデニン(A)が全塩基の <b>30%</b> を占めていた。</p>
              <p>このDNAに含まれるグアニン(G)の割合はいくらか。</p>`,
    type: "radio",
    options: ["10%", "20%", "30%", "40%"],
    answer: 1, // 20%
    hints: [
      "二本鎖DNAでは A は必ず T と、G は必ず C と対を作ります。ということは A と T の量はどうなりますか。",
      "A = T = 30% とすると、残りの 100 − 30 − 30 = 40% を G と C が等しく分け合います。",
    ],
    solution: `<p><b>【ステップ1】シャルガフの法則を確認する</b></p>
               <p>二本鎖DNAでは相補的な塩基対（A–T、G–C）を作るため、必ず
               <b>A = T</b>、<b>G = C</b> が成り立ちます。</p>
               <p><b>【ステップ2】A と T の合計を求める</b></p>
               <p>A = 30% なので T も 30%。合わせて 60% です。</p>
               <p><b>【ステップ3】G と C に残りを配分する</b></p>
               <p>残りは 100 − 60 = 40%。これを G と C が等しく分け合うので、<br>
               G = C = 40 ÷ 2 = <b>20%</b></p>
               <p>したがって、正解は <b>20% (B)</b> です。</p>
               <p class="tiny">補足：一本鎖DNAやRNAではこの法則は成り立ちません。「二本鎖である」ことが前提です。</p>`,
  },

  {
    id: "dna02", ...CAT,
    title: "DNA鎖の方向性と逆平行", diff: 120, tags: ["逆平行", "5'→3'", "ホスホジエステル結合"],
    explain: `<p>DNAの二重らせんについて、<b>誤っている</b>記述はどれか。</p>`,
    type: "radio",
    options: [
      "2本の鎖は互いに逆向き（逆平行）に並んでいる。",
      "新しい鎖の合成は、常に 5'→3' の方向に進む。",
      "ヌクレオチドどうしは、糖の3'-OH と次のヌクレオチドのリン酸との間でホスホジエステル結合を作る。",
      "DNAポリメラーゼは、伸長中の鎖の 5' 末端に新しいヌクレオチドを付加する。",
    ],
    answer: 3, // 「5'末端に付加する」が誤り
    hints: [
      "DNAポリメラーゼが「どちらの末端に」ヌクレオチドを足すのかを思い出しましょう。",
      "反応のエネルギー源は、取り込まれる dNTP 側の三リン酸です。伸長端に必要なのは -OH 基です。",
    ],
    solution: `<p><b>【ステップ1】各選択肢を検討する</b></p>
               <ul>
                 <li><b>A 正しい</b>：一方の鎖が 5'→3' なら、もう一方は 3'→5' に走ります（逆平行）。</li>
                 <li><b>B 正しい</b>：既知のDNAポリメラーゼはすべて 5'→3' 方向にしか合成できません。</li>
                 <li><b>C 正しい</b>：3'-OH が次のヌクレオチドの α位リン酸を求核攻撃し、ピロリン酸が外れます。</li>
                 <li><b>D 誤り</b>：付加されるのは <b>3' 末端</b> です。</li>
               </ul>
               <p><b>【ステップ2】なぜ 3' 末端でなければならないのか</b></p>
               <p>伸長反応のエネルギーは、<b>取り込まれる dNTP が持つ三リン酸</b>の加水分解でまかなわれます。
               もし 5' 末端に付加する方式なら、エネルギーを持つ三リン酸は伸長中の鎖の側に必要になります。
               その場合、校正で末端を1個削るたびにエネルギー源も失われ、伸長を再開できません。<br>
               3'→5' エキソヌクレアーゼによる<b>校正機能</b>と両立させるために、5'→3' 合成でなければならないのです。</p>
               <p>したがって、誤っているのは <b>D</b> です。</p>`,
  },

  {
    id: "dna03", ...CAT,
    title: "メセルソン・スタールの実験", diff: 150, tags: ["半保存的複製", "密度勾配遠心", "古典的実験"],
    explain: `<p><sup>15</sup>N を含む培地で長く培養した大腸菌を、<sup>14</sup>N の培地に移して培養した。
              一定世代ごとにDNAを取り出し、塩化セシウム密度勾配遠心にかけた。</p>
              <p><b>2回</b>分裂したあとのDNAのバンドの見え方として正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "重いバンドと軽いバンドが 1 : 1",
      "中間のバンドのみ",
      "中間のバンドと軽いバンドが 1 : 1",
      "中間のバンドと軽いバンドが 1 : 3",
    ],
    answer: 2, // 中間と軽いが 1:1
    hints: [
      "1回目の分裂後は、すべてのDNAが「重い鎖1本＋軽い鎖1本」の中間の重さになります。",
      "2回目では、その中間DNA 2分子が 4分子になります。もとの重い鎖は2本しかないので、それを含む分子は2つだけです。",
    ],
    solution: `<p><b>【ステップ1】出発点を確認する</b></p>
               <p>最初のDNAは両鎖とも <sup>15</sup>N（重い–重い）です。</p>
               <p><b>【ステップ2】1回目の分裂</b></p>
               <p>半保存的複製では、親の2本鎖がそれぞれ鋳型になります。新しく作られる鎖は
               <sup>14</sup>N（軽い）なので、できる2分子はどちらも<b>重い–軽い（中間）</b>です。</p>
               <p><b>【ステップ3】2回目の分裂</b></p>
               <p>中間の分子2つが、それぞれ「重い鎖」と「軽い鎖」に分かれて鋳型になります。</p>
               <table>
                 <tr><th>鋳型</th><th>新生鎖</th><th>できる分子</th></tr>
                 <tr><td>重い鎖（2本）</td><td>軽い</td><td>中間 × 2</td></tr>
                 <tr><td>軽い鎖（2本）</td><td>軽い</td><td>軽い–軽い × 2</td></tr>
               </table>
               <p>合計4分子のうち、<b>中間が2、軽いが2</b>。よって <b>1 : 1</b> です。</p>
               <p><b>【他の選択肢】</b></p>
               <ul>
                 <li>A ⇒ 重い–重いの分子は1回目の分裂で消えるので、重いバンドは現れません。</li>
                 <li>B ⇒ 1回目の分裂後の結果です。</li>
                 <li>D ⇒ 3回目の分裂後（中間2：軽い6 = 1:3）の結果です。</li>
               </ul>
               <p>したがって、正解は <b>中間のバンドと軽いバンドが 1 : 1 (C)</b> です。</p>`,
  },

  {
    id: "dna04", ...CAT,
    title: "ヌクレオソームとクロマチン", diff: 165, tags: ["ヒストン", "ヌクレオソーム", "クロマチン"],
    explain: `<p>真核生物のクロマチンの基本単位であるヌクレオソームについて、正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "H1 を含む8量体に、約146塩基対のDNAが約1.65回巻きついている。",
      "H2A・H2B・H3・H4 が2分子ずつ集まった8量体に、約146塩基対のDNAが約1.65回巻きついている。",
      "ヒストンは酸性タンパク質で、DNAのリン酸基と静電的に結合している。",
      "ヌクレオソームどうしをつなぐリンカーDNAには、ヒストンは一切結合しない。",
    ],
    answer: 1,
    hints: [
      "コアヒストンは4種類。H1 はコアには含まれず、別の役割を持ちます。",
      "DNAは負に帯電しています。それと強く結合するタンパク質は、どちらの電荷を帯びているべきでしょう。",
    ],
    solution: `<p><b>【ステップ1】ヌクレオソームの構造を整理する</b></p>
               <ul>
                 <li><b>コアヒストン</b>：H2A, H2B, H3, H4 が<b>各2分子</b>＝<b>8量体</b></li>
                 <li>この周りに約 <b>146 bp</b> のDNAが約 <b>1.65回</b> 左巻きに巻きつく</li>
                 <li><b>H1（リンカーヒストン）</b>：コアには含まれず、DNAの出入り口とリンカーDNAに結合して
                     30nm繊維への凝縮を助ける</li>
               </ul>
               <p><b>【ステップ2】各選択肢を検討する</b></p>
               <ul>
                 <li>A ⇒ H1 は8量体に含まれません。誤り。</li>
                 <li><b>B ⇒ 正しい。</b></li>
                 <li>C ⇒ ヒストンはリジン・アルギニンに富む<b>塩基性</b>タンパク質です。正に帯電しているからこそ、
                     負電荷のDNAリン酸骨格と強く結合します。「酸性」が誤り。</li>
                 <li>D ⇒ リンカーDNAには H1 が結合します。誤り。</li>
               </ul>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "dna05", ...CAT,
    title: "岡崎フラグメントとラギング鎖", diff: 200, tags: ["DNA複製", "岡崎フラグメント", "DNAリガーゼ"],
    explain: `<p>複製フォークでは、一方の鎖は連続的に、もう一方は不連続に合成される。</p>
              <p>ラギング鎖の合成に関わる次の出来事を、<b>起こる順に</b>並べたものはどれか。</p>
              <ol style="margin:8px 0">
                <li>DNAリガーゼによる連結</li>
                <li>プライマーゼによるRNAプライマーの合成</li>
                <li>DNAポリメラーゼδ による伸長</li>
                <li>RNアーゼH・FEN1 などによるRNAプライマーの除去と穴埋め</li>
              </ol>`,
    type: "radio",
    options: ["2 → 3 → 4 → 1", "2 → 4 → 3 → 1", "3 → 2 → 4 → 1", "2 → 3 → 1 → 4"],
    answer: 0,
    hints: [
      "DNAポリメラーゼは何もないところから合成を始められません。まず何が必要ですか。",
      "最後に残るのは「切れ目（ニック）」です。それをふさぐ酵素はどれでしょう。",
    ],
    solution: `<p><b>【ステップ1】DNAポリメラーゼの制約を思い出す</b></p>
               <p>DNAポリメラーゼは <b>3'-OH がないと伸長を開始できません</b>。
               そこでまず <b>プライマーゼ</b>（RNAポリメラーゼの一種）が約10塩基のRNAプライマーを作ります（2）。</p>
               <p><b>【ステップ2】伸長</b></p>
               <p>そのプライマーの 3'末端から、<b>DNAポリメラーゼδ</b> が次の岡崎フラグメントにぶつかるまで伸長します（3）。
               真核生物の岡崎フラグメントは約 100〜200 塩基です。</p>
               <p><b>【ステップ3】RNAを取り除いてDNAで埋める</b></p>
               <p>RNAのままでは残せないので、RNアーゼH と FEN1 がプライマーを除去し、
               DNAポリメラーゼが同じ場所をDNAで埋めます（4）。</p>
               <p><b>【ステップ4】ニックをふさぐ</b></p>
               <p>最後に残った 3'-OH と 5'-リン酸のあいだの切れ目を、<b>DNAリガーゼ</b>が
               ホスホジエステル結合でつなぎます（1）。</p>
               <p>よって順序は <b>2 → 3 → 4 → 1 (A)</b> です。</p>`,
  },

  {
    id: "dna06", ...CAT,
    title: "末端複製問題とテロメラーゼ", diff: 225, tags: ["テロメア", "テロメラーゼ", "逆転写酵素"],
    explain: `<p>直鎖状染色体の末端では、複製のたびに短くなってしまう「末端複製問題」が生じる。</p>
              <p>この問題と、それに対処するテロメラーゼについて、<b>誤っている</b>記述はどれか。</p>`,
    type: "radio",
    options: [
      "末端が短くなるのは、ラギング鎖の最も末端のRNAプライマーを除去したあと、そこを埋める 3'-OH が存在しないためである。",
      "テロメラーゼは自身のRNAを鋳型としてDNAを合成する、逆転写酵素の一種である。",
      "テロメラーゼはリーディング鎖側の 5' 末端を直接伸長することで問題を解決している。",
      "ヒトの多くの体細胞ではテロメラーゼ活性が低く、分裂のたびにテロメアが短縮していく。",
    ],
    answer: 2,
    hints: [
      "テロメラーゼが伸ばすのは、どちらの鎖のどちらの末端でしょうか。3' 側の突出（Gテール）を思い出しましょう。",
      "テロメラーゼが伸ばしたあと、相補鎖はふつうのプライマーゼ＋DNAポリメラーゼで埋められます。",
    ],
    solution: `<p><b>【ステップ1】なぜ末端が短くなるのか</b></p>
               <p>ラギング鎖では、最末端のRNAプライマーを除去したあとに残る隙間を埋めようにも、
               その外側に <b>3'-OH を提供する鎖がありません</b>。DNAポリメラーゼは
               5'→3' にしか伸ばせず、既存の 3' 末端を必要とするためです（A は正しい）。</p>
               <p><b>【ステップ2】テロメラーゼの働き</b></p>
               <p>テロメラーゼは <b>TERT</b>（触媒サブユニット）と <b>TERC</b>（鋳型RNA）からなる
               リボヌクレオタンパク質で、自身が持つRNAを鋳型にDNAを合成する
               <b>逆転写酵素</b>です（B は正しい）。</p>
               <p>伸ばすのは <b>3' 突出末端（Gテール）</b>です。テロメラーゼが 3' 側を伸長したあと、
               相補鎖は通常のプライマーゼ＋DNAポリメラーゼで合成されます。
               <b>5' 末端を直接伸長するのではありません</b>。</p>
               <p><b>【ステップ3】体細胞での状況</b></p>
               <p>生殖細胞・幹細胞・多くのがん細胞ではテロメラーゼが活性化していますが、
               通常の体細胞では活性が低く、分裂ごとに短縮して分裂寿命（ヘイフリック限界）につながります（D は正しい）。</p>
               <p>したがって、誤っているのは <b>C</b> です。</p>`,
  },

  {
    id: "dna07", ...CAT,
    title: "校正機能と複製の正確さ", diff: 245, tags: ["校正", "3'→5'エキソヌクレアーゼ", "変異率"],
    explain: `<p>DNAポリメラーゼが塩基を選ぶだけの段階での誤り率はおよそ <b>10<sup>-5</sup></b> である。
              これに 3'→5' エキソヌクレアーゼによる校正が加わると誤り率は約 <b>100分の1</b> に、
              さらにミスマッチ修復が働くと、さらに約 <b>100分の1</b> になる。</p>
              <p>最終的な複製の誤り率として最も近いものはどれか。</p>`,
    type: "radio",
    options: ["10<sup>-7</sup>", "10<sup>-9</sup>", "10<sup>-11</sup>", "10<sup>-13</sup>"],
    answer: 1,
    hints: [
      "「100分の1になる」は 10<sup>-2</sup> を掛けることと同じです。",
      "10<sup>-5</sup> × 10<sup>-2</sup> × 10<sup>-2</sup> を計算しましょう。",
    ],
    solution: `<p><b>【ステップ1】三段階の精度を掛け合わせる</b></p>
               <table>
                 <tr><th>段階</th><th>寄与</th><th>累積の誤り率</th></tr>
                 <tr><td>塩基選択（幾何学的な選別）</td><td>—</td><td>10<sup>-5</sup></td></tr>
                 <tr><td>3'→5' エキソヌクレアーゼによる校正</td><td>×10<sup>-2</sup></td><td>10<sup>-7</sup></td></tr>
                 <tr><td>ミスマッチ修復（MMR）</td><td>×10<sup>-2</sup></td><td><b>10<sup>-9</sup></b></td></tr>
               </table>
               <p><b>【ステップ2】意味を確かめる</b></p>
               <p>10<sup>-9</sup> は「10億塩基に1回の誤り」です。ヒトゲノムは約 3×10<sup>9</sup> 塩基対なので、
               1回の複製あたり数個の誤りという計算になり、実測ともよく合います。</p>
               <p><b>【ステップ3】各段階の役割を区別する</b></p>
               <p>校正は<b>合成中に</b>、取り込んだばかりの誤った塩基を同じ酵素が削る仕組みです。
               一方ミスマッチ修復は<b>合成後に</b>、新生鎖側を見分けて（大腸菌では Dam メチル化の有無、
               真核生物では PCNA やニックを手がかりに）切り出し、作り直します。</p>
               <p>したがって、正解は <b>10<sup>-9</sup> (B)</b> です。</p>`,
  },

  {
    id: "dna08", ...CAT,
    title: "DNA修復経路の使い分け", diff: 255, tags: ["ヌクレオチド除去修復", "塩基除去修復", "色素性乾皮症"],
    explain: `<p>次の損傷と、それを主に処理する修復経路の組み合わせとして、<b>最も適切</b>なものはどれか。</p>
              <table>
                <tr><th>損傷</th><th>経路</th></tr>
                <tr><td>紫外線によるチミン二量体</td><td>?</td></tr>
                <tr><td>シトシンの脱アミノによるウラシル</td><td>?</td></tr>
              </table>`,
    type: "radio",
    options: [
      "塩基除去修復（BER） ／ ヌクレオチド除去修復（NER）",
      "ヌクレオチド除去修復（NER） ／ 塩基除去修復（BER）",
      "ミスマッチ修復（MMR） ／ 相同組換え（HR）",
      "相同組換え（HR） ／ ミスマッチ修復（MMR）",
    ],
    answer: 1,
    hints: [
      "らせん構造を大きく歪める「かさばった損傷」と、塩基1個だけの化学変化。それぞれに向いた経路があります。",
      "色素性乾皮症（XP）の患者は紫外線に極端に弱くなります。どの経路が壊れているでしょうか。",
    ],
    solution: `<p><b>【ステップ1】損傷の性質で経路が決まる</b></p>
               <ul>
                 <li><b>チミン二量体</b>：隣り合うピリミジンが共有結合し、二重らせんを大きく<b>歪ませる</b>。
                     こうした「かさばった損傷」は <b>ヌクレオチド除去修復（NER）</b> が
                     損傷を含む約 24〜32 塩基のオリゴヌクレオチドごと切り出して処理する。</li>
                 <li><b>ウラシル</b>：シトシンの脱アミノで生じる。らせんは大きく歪まず、塩基1個の化学的な変化。
                     <b>塩基除去修復（BER）</b> が、ウラシルDNAグリコシラーゼで塩基だけを切り離し、
                     生じた AP部位を APエンドヌクレアーゼが処理する。</li>
               </ul>
               <p><b>【ステップ2】臨床から確かめる</b></p>
               <p><b>色素性乾皮症（XP）</b>は NER の欠損症で、紫外線によるチミン二量体を除去できず、
               露光部の皮膚がんリスクが著しく上がります。これが「紫外線損傷 = NER」の裏づけです。</p>
               <p><b>【他の選択肢】</b></p>
               <ul>
                 <li>C・D ⇒ MMR は複製時の取り込み誤り、HR は二本鎖切断を扱う経路で、いずれも本問の損傷には対応しません。
                     なお MMR 欠損はリンチ症候群、HR に関わる BRCA1/2 の変異は乳がん・卵巣がんと関連します。</li>
               </ul>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "dna09", ...CAT,
    title: "複製起点の数を見積もる", diff: 285, tags: ["複製起点", "計算", "定量的推論"],
    explain: `<p>あるヒト細胞のゲノムは <b>6.0 × 10<sup>9</sup></b> 塩基対（2倍体）である。
              複製フォークの進行速度を <b>50 塩基対/秒</b>、S期の長さを <b>8時間</b> とする。</p>
              <p>すべてのDNAをS期のあいだに複製し終えるために必要な複製起点の数は、最低でおよそいくつか。
              なお、1つの複製起点からは<b>両方向に2つ</b>のフォークが進むものとする。</p>
              <p>半角数字で入力してください（例：<code>2000</code>）。おおよその値で構いません。</p>`,
    type: "text",
    answer: ["2083", "2084", "2085", "2000", "2100"],
    hints: [
      "まず、1つのフォークが8時間で進める距離を求めましょう。8時間は何秒ですか。",
      "起点1つにつきフォークは2つ。つまり起点1つがカバーできる塩基対数は、1フォークぶんの2倍です。",
    ],
    solution: `<p><b>【ステップ1】S期の長さを秒に直す</b></p>
               <p>8 時間 = 8 × 60 × 60 = <b>28,800 秒</b></p>
               <p><b>【ステップ2】フォーク1つが進む距離</b></p>
               <p>50 bp/秒 × 28,800 秒 = <b>1.44 × 10<sup>6</sup> bp</b></p>
               <p><b>【ステップ3】起点1つがカバーする距離</b></p>
               <p>起点からは両方向に2つのフォークが出るので、<br>
               1.44 × 10<sup>6</sup> × 2 = <b>2.88 × 10<sup>6</sup> bp</b></p>
               <p><b>【ステップ4】必要な起点数</b></p>
               <p>6.0 × 10<sup>9</sup> ÷ (2.88 × 10<sup>6</sup>) ≒ <b>2,083</b></p>
               <p>つまり<b>およそ2,000個</b>の複製起点が最低限必要です。</p>
               <p><b>【ステップ5】現実と照らす</b></p>
               <p>実際のヒト細胞にはおよそ 3〜5万個の複製起点があると見積もられています。
               計算値よりずっと多いのは、(1) すべての起点が同時に発火するわけではなく S期を通じて
               時間差で活性化すること、(2) 損傷でフォークが止まったときの予備（休眠起点）を
               確保していること、によります。<br>
               この「計算上の最低値と実測値のギャップ」自体が、複製がいかに冗長に設計されているかを示しています。</p>
               <p>したがって、答えはおよそ <b>2,000</b> です。</p>`,
  },

  {
    id: "dna10", ...CAT,
    title: "二本鎖切断修復の選択と変異体の表現型", diff: 305, tags: ["相同組換え", "NHEJ", "細胞周期", "実験推論"],
    explain: `<p>DNA二本鎖切断（DSB）の修復には、相同組換え（HR）と非相同末端結合（NHEJ）がある。</p>
              <p>ある培養細胞から、<b>HRに必須の因子を欠損した変異株</b>を作製し、
              放射線を当ててDSBを誘発したときの挙動を調べた。予想される結果として最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "G1期の細胞で特に感受性が高くなり、S/G2期の細胞では野生型とほぼ同じ生存率を示す。",
      "S/G2期の細胞で特に感受性が高くなり、修復されたとしても挿入・欠失を伴う不正確な修復が増える。",
      "細胞周期のどの時期でも感受性は変わらず、修復は完全に停止する。",
      "NHEJ が代償的に働くため、野生型と区別できない生存率を示す。",
    ],
    answer: 1,
    hints: [
      "HR は「無傷の相同配列」を鋳型に使います。その鋳型、つまり姉妹染色分体があるのは細胞周期のどの時期でしょう。",
      "HR が使えないとき、細胞はどちらの経路に頼らざるを得ませんか。その経路の正確さは？",
    ],
    solution: `<p><b>【ステップ1】HR と NHEJ の使い分けを整理する</b></p>
               <table>
                 <tr><th></th><th>相同組換え（HR）</th><th>非相同末端結合（NHEJ）</th></tr>
                 <tr><td>鋳型</td><td>姉妹染色分体が必要</td><td>不要</td></tr>
                 <tr><td>働く時期</td><td><b>S / G2期</b></td><td>細胞周期を通じて（特に G1期）</td></tr>
                 <tr><td>正確さ</td><td>ほぼ正確</td><td><b>不正確</b>（挿入・欠失を伴いうる）</td></tr>
                 <tr><td>主な因子</td><td>BRCA1/2, RAD51, MRN</td><td>Ku70/80, DNA-PKcs, LIG4</td></tr>
               </table>
               <p><b>【ステップ2】HR欠損株で何が起こるか</b></p>
               <p>HR が働くのは<b>姉妹染色分体という鋳型が存在する S/G2期</b>です。
               したがって HR を失うと、<b>S/G2期の細胞がとりわけ放射線に弱くなります</b>。
               G1期はもともと NHEJ が主役なので、影響は相対的に小さくなります。</p>
               <p><b>【ステップ3】修復の質はどうなるか</b></p>
               <p>HR が使えない細胞は NHEJ に頼らざるを得ません。NHEJ は切断端を削って直接つなぐため、
               <b>小さな挿入・欠失（indel）が生じやすい</b>。よって「生存はしても変異が増える」ことになります。</p>
               <p><b>【他の選択肢】</b></p>
               <ul>
                 <li>A ⇒ 感受性が高くなる時期が逆です。G1期はもともと HR を使っていません。</li>
                 <li>C ⇒ NHEJ は無傷なので、修復が完全に止まることはありません。</li>
                 <li>D ⇒ NHEJ が一部を代償するのは確かですが、HR欠損細胞は明らかに放射線感受性が高くなります。</li>
               </ul>
               <p><b>【発展】</b>この性質は臨床に直結します。BRCA1/2 変異のがん細胞は HR 欠損なので、
               一本鎖切断修復に関わる PARP を阻害すると、複製時に修復不能な DSB が蓄積して選択的に死にます
               （<b>合成致死</b>）。これが PARP阻害剤の作用原理です。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "dna11", ...CAT,
    title: "複製に働く酵素の順番", diff: 135, tags: ["複製", "ヘリカーゼ", "プライマーゼ"],
    explain: `<p>DNA複製フォークで働く酵素を、<b>働く順番</b>に並べたものとして正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "ヘリカーゼ → プライマーゼ → DNAポリメラーゼ → DNAリガーゼ",
      "プライマーゼ → ヘリカーゼ → DNAリガーゼ → DNAポリメラーゼ",
      "DNAポリメラーゼ → ヘリカーゼ → プライマーゼ → DNAリガーゼ",
      "DNAリガーゼ → プライマーゼ → ヘリカーゼ → DNAポリメラーゼ",
    ],
    answer: 0,
    hints: [
      "まず二本鎖をほどかないと何も始まりません。",
      "DNAポリメラーゼは、何もないところから鎖を作り始めることができません。",
    ],
    solution: `<p><b>【ステップ1】流れを物語として覚える</b></p>
               <ol>
                 <li><b>ヘリカーゼ</b>：二本鎖の水素結合を切ってほどく（一本鎖結合タンパク質が再結合を防ぐ）</li>
                 <li><b>プライマーゼ</b>：RNAプライマーを合成する<br>
                 　→ DNAポリメラーゼは<b>3'-OH がなければ伸長できない</b>ため、足場が必要</li>
                 <li><b>DNAポリメラーゼ</b>：5'→3' 方向に伸長する</li>
                 <li><b>DNAリガーゼ</b>：岡崎フラグメント間の切れ目（ニック）をつなぐ</li>
               </ol>
               <p><b>【ステップ2】リーディングとラギング</b></p>
               <p>ポリメラーゼは<b>5'→3' にしか伸ばせない</b>ため、
               連続的に合成できる<b>リーディング鎖</b>と、
               短い<b>岡崎フラグメント</b>を繰り返し作る<b>ラギング鎖</b>に分かれます。
               ラギング鎖ではプライマーゼが何度も呼ばれ、リガーゼの出番も多くなります。</p>
               <p><b>【ステップ3】大腸菌のポリメラーゼ</b></p>
               <table>
                 <tr><th>酵素</th><th>役割</th></tr>
                 <tr><td><b>DNAポリメラーゼIII</b></td><td>複製の<b>主役</b>。高速・高い連続性で新生鎖を伸ばす。3'→5' 校正活性あり</td></tr>
                 <tr><td>DNAポリメラーゼI</td><td>RNAプライマーの除去（5'→3' エキソヌクレアーゼ活性）とその埋め合わせ</td></tr>
               </table>
               <p>なお「複製に関与しない酵素はどれか」という問いでは、
               <b>DNAグルコシダーゼ</b>のような実在しない／無関係な名前が混ぜられます。落ち着いて選んでください。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "dna12", ...CAT,
    title: "ピリミジンダイマーとヌクレオチド除去修復", diff: 280, tags: ["紫外線", "NER", "色素性乾皮症"],
    explain: `<p>紫外線によって隣接するチミン間に<b>シクロブタン型ピリミジンダイマー</b>が生じる。</p>
              <p>ヒトでこの損傷を取り除く修復系と、その因子の組み合わせとして正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "ヌクレオチド除去修復。XPB/XPD がヘリカーゼとして二本鎖を開き、XPG と XPF/ERCC1 がエンドヌクレアーゼとして損傷部の両側を切断する。",
      "塩基除去修復。DNAグリコシラーゼが損傷塩基を切り出し、APエンドヌクレアーゼが働く。",
      "光回復酵素（フォトリアーゼ）が可視光のエネルギーでダイマーを直接開裂させる。",
      "ミスマッチ修復。MSH2/MLH1 が誤対合を認識して除去する。",
    ],
    answer: 0,
    hints: [
      "ダイマーは「塩基が1つおかしい」のではなく「二重らせんが歪む大きな傷」です。修復系の選び方が決まります。",
      "ヒトには光回復酵素がありません。",
    ],
    solution: `<p><b>【ステップ1】どの修復系を使うか</b></p>
               <table>
                 <tr><th>修復系</th><th>対象</th></tr>
                 <tr><td><b>ヌクレオチド除去修復 (NER)</b></td><td><b>らせんを歪ませる大きな損傷</b>（ピリミジンダイマー、化学物質の付加体）</td></tr>
                 <tr><td>塩基除去修復 (BER)</td><td>小さな塩基の変化（脱アミノ、酸化、アルキル化）</td></tr>
                 <tr><td>ミスマッチ修復 (MMR)</td><td>複製エラーによる誤対合</td></tr>
               </table>
               <p>ピリミジンダイマーは隣接する2塩基が<b>共有結合</b>で繋がり、
               らせんが大きく歪むため <b>NER</b> の担当です。</p>
               <p><b>【ステップ2】NER の手順と因子</b></p>
               <ol>
                 <li><b>認識</b>：XPC など（転写と共役する経路もある）</li>
                 <li><b>開裂</b>：<b>XPB・XPD（ヘリカーゼ、TFIIHの構成因子）</b>が二本鎖を開く</li>
                 <li><b>切断</b>：<b>XPG が3'側、XPF/ERCC1 が5'側</b>を切る → 24〜32塩基のオリゴヌクレオチドとして除去</li>
                 <li><b>再合成</b>：DNAポリメラーゼが埋め、<b>DNAリガーゼ</b>が繋ぐ</li>
               </ol>
               <p>「ヘリカーゼ＝XPB/XPD、ヌクレアーゼ＝XPG と XPF/ERCC1」という役割分担が問われます。</p>
               <p><b>【ステップ3】色素性乾皮症（XP）</b></p>
               <p>これら XP 遺伝子群の変異で NER が働かなくなる常染色体潜性の疾患です。</p>
               <ul>
                 <li>日光過敏、幼少期からの高度の<b>色素沈着・乾燥</b></li>
                 <li><b>皮膚がんの発症率が著しく高い</b>（数千倍）</li>
                 <li>一部の病型では神経症状を伴う</li>
                 <li>治療は根治的でなく、<b>徹底した遮光</b>が中心</li>
               </ul>
               <p>なお C の光回復酵素は細菌や一部の生物にはありますが、<b>ヒトは持ちません</b>。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "dna13", ...CAT,
    title: "自然に起こるDNAの化学変化", diff: 205, tags: ["脱アミノ", "脱プリン", "変異"],
    explain: `<p>外的な要因がなくても、DNAには自然に化学変化が起こる。</p>
              <p><b>シトシンの脱アミノ反応</b>が修復されないまま複製されると、どのような塩基置換が生じるか。</p>`,
    type: "radio",
    options: [
      "C:G → T:A",
      "C:G → A:T",
      "A:T → G:C",
      "A:T → T:A",
    ],
    answer: 0,
    hints: [
      "シトシンからアミノ基が外れると、どの塩基になるでしょうか。",
      "できた塩基は、複製のときに何と対合しますか。",
    ],
    solution: `<p><b>【ステップ1】脱アミノで何になるか</b></p>
               <table>
                 <tr><th>元の塩基</th><th>脱アミノ後</th><th>対合相手</th></tr>
                 <tr><td><b>シトシン (C)</b></td><td><b>ウラシル (U)</b></td><td>A</td></tr>
                 <tr><td><b>5-メチルシトシン</b></td><td><b>チミン (T)</b></td><td>A</td></tr>
                 <tr><td>アデニン (A)</td><td>ヒポキサンチン</td><td>C</td></tr>
                 <tr><td>グアニン (G)</td><td>キサンチン</td><td>—</td></tr>
               </table>
               <p>C が U になると、複製時に U は A と対合します。
               結果として <b>C:G → T:A</b> の置換（トランジション）が固定されます。</p>
               <p><b>【ステップ2】なぜDNAはTを使うのか</b></p>
               <p>ここが本質的な問いです。DNA に U ではなく T を使うのは、
               <b>U を見つけたら「それは壊れた C だ」と判定できる</b>ようにするためです。
               ウラシルDNAグリコシラーゼが U を切り出し、BER で C に戻します。
               もし DNA が U を正規の塩基として使っていたら、この区別ができません。</p>
               <p><b>【ステップ3】5-メチルシトシンの弱点</b></p>
               <p>5mC が脱アミノすると<b>T</b> になります。T は DNA の正規の塩基なので、
               「壊れた C」と認識されにくく、修復から漏れやすい。
               このため <b>CpG 部位は変異のホットスポット</b>となり、
               ゲノム中の CpG が進化的に減少した原因にもなっています。</p>
               <p><b>【ステップ4】脱プリン反応</b></p>
               <p>プリン塩基（A・G）とデオキシリボースのグリコシド結合が自然に切れる反応で、
               ヒトの細胞で1日あたり数千〜1万か所起こると見積もられています。
               生じた<b>脱塩基部位（APサイト）</b>は BER で修復されます。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "dna14", ...CAT,
    title: "テロメアの構造とシェルタリン", diff: 215, tags: ["テロメア", "シェルタリン", "TRF2", "Gテール"],
    explain: `<p>ヒトのテロメアは <b>TTAGGG</b> の繰り返しからなり、
              3' 側が一本鎖として突出している（Gテール）。
              ここに<b>シェルタリン</b>複合体（TRF1, TRF2, POT1, TIN2, TPP1, RAP1）が結合している。</p>
              <p>シェルタリンの最も重要な役割はどれか。</p>`,
    type: "radio",
    options: [
      "テロメア配列を転写して、テロメラーゼの鋳型RNAを供給する。",
      "染色体末端を「DNA二本鎖切断」と認識させないよう覆い隠し、損傷応答や末端融合を防ぐ。",
      "テロメアを積極的に切断して、細胞老化を早める。",
      "テロメア領域のヒストンを除去し、ユークロマチンに保つ。",
    ],
    answer: 1,
    hints: [
      "細胞にとって、染色体の末端は「切れたDNAの端」と区別がつきません。",
      "TRF2 を機能阻害すると、染色体末端どうしが融合した異常核型が現れます。",
    ],
    solution: `<p><b>【ステップ1】末端は「切断」とまぎらわしい</b></p>
               <p>DNA二本鎖切断の修復機構（ATM/ATR、NHEJ、HR）は、
               DNAの自由端を見つけると働きます。
               ところが直鎖状染色体には、正常な状態でも末端が46本×2＝92か所あります。
               ここに損傷応答が働いてしまえば、
               細胞周期は止まり、末端どうしが<b>NHEJで融合</b>してしまいます。</p>
               <p><b>【ステップ2】シェルタリンが「ここは末端です」と宣言する</b></p>
               <table>
                 <tr><th>因子</th><th>結合部位</th><th>働き</th></tr>
                 <tr><td>TRF1 / TRF2</td><td>二本鎖 TTAGGG</td><td>ATM の活性化と NHEJ を抑える。TRF2 は<b>t-loop</b>形成を促す</td></tr>
                 <tr><td>POT1（TPP1と）</td><td><b>一本鎖 Gテール</b></td><td>ATR の活性化を抑え、テロメラーゼの働きを調節する</td></tr>
                 <tr><td>TIN2</td><td>橋渡し</td><td>二本鎖側と一本鎖側の因子を連結</td></tr>
               </table>
               <p>TRF2 を失わせると染色体末端融合が起こり、
               POT1 を失わせると ATR 依存の損傷応答が起こる。
               二本鎖側と一本鎖側で監視役が分かれていることが、この実験から読み取れます。</p>
               <p><b>【ステップ3】保護の解除がシグナルになる</b></p>
               <p>テロメアが短くなるとシェルタリンが乗る足場が減り、
               保護が破れて損傷応答が発動します。
               これが<b>複製老化（ヘイフリック限界）</b>の分子的な引き金です。
               「長さそのもの」ではなく「保護が保てるかどうか」が読み取られている、
               という理解が重要です。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "dna15", ...CAT,
    title: "テロメア短縮の2つの帰路 ─ 老化とクライシス", diff: 275, tags: ["テロメア", "細胞老化", "クライシス", "p53", "染色体不安定性"],
    explain: `<p>正常ヒト線維芽細胞を培養しつづけると、約50回の分裂で増殖を停止する（複製老化）。
              ところが p53 と Rb 経路を不活化した細胞は、そこを越えてさらに分裂し、
              やがて染色体の末端融合や多倍体化を伴う大量死（<b>クライシス</b>）に至る。</p>
              <p>この観察から言えることはどれか。</p>`,
    type: "radio",
    options: [
      "複製老化はテロメア短縮への「応答」であり、短縮そのものを止める機構ではない。応答を壊すと短縮は進み、染色体不安定性が生じる。",
      "p53 と Rb がテロメアを短縮させているので、これらを壊すとテロメアは短縮しなくなる。",
      "クライシスは培地の栄養不足によるもので、テロメアとは関係がない。",
      "複製老化を越えた細胞は必ず不死化するので、クライシスは起こらない。",
    ],
    answer: 0,
    hints: [
      "p53/Rb 経路は「損傷を受け取って止まる」側の装置です。壊すと何が起きるでしょうか。",
      "保護されない末端どうしが NHEJ でつながると、次の分裂で何が起こるか考えてみましょう。",
    ],
    solution: `<p><b>【ステップ1】老化はブレーキ、短縮はアクセル</b></p>
               <p>テロメアが限界長に達すると保護が破れ（→ dna14）、
               ATM/ATR → <b>p53</b> → p21、および p16-<b>Rb</b> の経路が働いて
               細胞は不可逆的に分裂を停止します（M1：複製老化）。
               これは短縮を止める機構ではなく、短縮を<b>検知して停止する</b>機構です。</p>
               <p><b>【ステップ2】ブレーキを壊すと何が起こるか</b></p>
               <p>p53/Rb を不活化すると（SV40 large T、HPV E6/E7 など）
               細胞は停止せずに分裂を続け、テロメアはさらに短くなります。
               保護を失った末端は NHEJ で互いに融合し、</p>
               <ul>
                 <li>二動原体染色体ができる</li>
                 <li>分裂期に両極へ引かれて<b>アナフェーズブリッジ</b>となり切断される</li>
                 <li>切れた末端がまた融合する ─ <b>切断融合橋サイクル</b></li>
               </ul>
               <p>結果として大規模な染色体再編成と大量の細胞死が起こります（M2：クライシス）。</p>
               <p><b>【ステップ3】ここから生き延びた細胞がやること</b></p>
               <p>クライシスを脱する細胞はごく稀で、そのほぼすべてが
               <b>テロメア維持機構を再獲得</b>しています（テロメラーゼの再活性化、または ALT）。
               ヒトがんの約90%で TERT が再発現しているのはこの帰結です。</p>
               <p>つまりテロメア短縮は<b>がん抑制機構</b>（分裂回数の上限）でありながら、
               ブレーキが壊れた文脈では<b>染色体不安定性の発生源</b>にもなります。
               この二面性が、老化とがんが同じ装置でつながっている理由です。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "dna16", ...CAT,
    title: "テロメラーゼを使わないテロメア維持 ─ ALT", diff: 295, tags: ["ALT", "テロメラーゼ", "相同組換え", "ATRX"],
    explain: `<p>あるがん細胞株はテロメラーゼ活性が検出限界以下であるにもかかわらず、
              長期培養してもテロメアが短縮しない。
              調べると、テロメア長が細胞ごとに極端に不均一で、
              <b>ALT関連PML体（APB）</b>が観察された。</p>
              <p>この細胞のテロメア維持機構として最も考えられるものはどれか。</p>`,
    type: "radio",
    options: [
      "テロメラーゼの鋳型RNA（TERC）だけで、タンパク質なしにテロメアを伸長している。",
      "テロメア配列を鋳型とした相同組換え依存の複製（ALT）によって伸長している。",
      "テロメアを失った染色体を環状化して末端問題を回避している。",
      "細胞分裂を完全に停止しており、複製が起きていないだけである。",
    ],
    answer: 1,
    hints: [
      "テロメラーゼがなくても、テロメアは互いに同じ繰り返し配列をもっています。相同配列があれば使える機構は何でしょう。",
      "長さが「極端に不均一」であることが、鋳型を借りるしくみを示唆します。",
    ],
    solution: `<p><b>【ステップ1】テロメア維持の2つのルート</b></p>
               <table>
                 <tr><th></th><th>テロメラーゼ経路</th><th>ALT</th></tr>
                 <tr><td>機構</td><td>TERT が TERC を鋳型に逆転写</td><td><b>相同組換え依存の複製</b></td></tr>
                 <tr><td>鋳型</td><td>自身のRNA</td><td><b>他の染色体や姉妹染色分体のテロメア</b></td></tr>
                 <tr><td>テロメア長</td><td>比較的均一に保たれる</td><td><b>極端に不均一（超長〜超短）</b></td></tr>
                 <tr><td>目印</td><td>TERT 発現、TRAPアッセイ陽性</td><td>APB、C環（extrachromosomal C-circle）</td></tr>
                 <tr><td>頻度</td><td>ヒトがんの約85〜90%</td><td>約10〜15%（骨・軟部腫瘍、グリオーマに多い）</td></tr>
               </table>
               <p><b>【ステップ2】不均一さが決定的な手がかり</b></p>
               <p>テロメラーゼは各末端を少しずつ伸ばすので長さが揃います。
               一方 ALT は「他のテロメアを鋳型に借りてコピーする」ため、
               伸長量が確率的で、細胞ごと・染色体ごとに長さが大きくばらつきます。
               サザンブロット（TRF法）で幅の広いスメアとして見えるのが典型です。</p>
               <p><b>【ステップ3】ALT が生じる背景</b></p>
               <p>ALT腫瘍では <b>ATRX</b> や <b>DAXX</b>（テロメアのクロマチン維持に働く）の
               機能喪失が高頻度に見られます。
               テロメアのクロマチンが不安定になり、
               複製ストレスと組換えが起こりやすくなることが引き金と考えられています。</p>
               <p class="tiny">臨床的な意味：テロメラーゼ阻害薬を開発しても、ALT型腫瘍には効きません。
               「テロメア維持を止める」治療戦略は、どちらの経路かを見極める必要があります。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "dna17", ...CAT,
    title: "テロメラーゼ活性はどこで必要か", diff: 165, tags: ["テロメラーゼ", "幹細胞", "TERT", "がん"],
    explain: `<p>ヒトの細胞におけるテロメラーゼ活性について、正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "すべての体細胞で高く保たれており、がんでは逆に低下している。",
      "生殖細胞・幹細胞・多くのがん細胞で高く、分化した体細胞では低い。",
      "赤血球で最も高い。",
      "テロメラーゼは胎児期にのみ存在し、出生後は完全に失われる。",
    ],
    answer: 1,
    hints: [
      "「一生分裂しつづける必要がある細胞」はどれでしょうか。",
      "がんが無限に増殖するには、テロメアの問題を解決する必要があります。",
    ],
    solution: `<p><b>【ステップ1】どの細胞が長期の分裂能を必要とするか</b></p>
               <table>
                 <tr><th>細胞</th><th>テロメラーゼ</th><th>理由</th></tr>
                 <tr><td>生殖細胞</td><td><b>高い</b></td><td>次世代に完全長のテロメアを渡す必要がある</td></tr>
                 <tr><td>造血・腸上皮などの幹細胞</td><td>中程度</td><td>生涯にわたる組織再生。ただし加齢とともに短縮する</td></tr>
                 <tr><td>分化した体細胞</td><td><b>低い／不活性</b></td><td>分裂回数に上限を設けている</td></tr>
                 <tr><td>がん細胞</td><td><b>高い（約90%）</b></td><td>無限増殖の前提条件</td></tr>
               </table>
               <p><b>【ステップ2】体細胞で低いことの意味</b></p>
               <p>テロメア短縮は分裂回数のカウンターとして働き、
               異常な細胞がむやみに増えるのを妨げます。
               言い換えれば、体細胞でテロメラーゼをオフにしていること自体が
               <b>がん抑制のしくみ</b>です。多細胞生物が支払っているコストが細胞老化です。</p>
               <p><b>【ステップ3】がんによる再活性化</b></p>
               <p>多くのがんで <b>TERT プロモーターの変異</b>（−124C>T など）が見つかり、
               転写因子の結合部位が新たに作られて TERT が再発現します。
               メラノーマ、グリオーマ、膀胱がんで特に高頻度です。</p>
               <p class="tiny">テロメラーゼの機能低下は、再生不良性貧血や肺線維症を伴う
               <b>先天性テロメア症候群</b>（<i>TERT</i>, <i>TERC</i>, <i>DKC1</i> 変異）を引き起こします。
               高すぎればがん、低すぎれば組織の枯渇 ─ 適量が要求される装置です。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },
];
